import React, { useState } from 'react';
import { Download, Music2, Mic2, Disc3, Film, Loader2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { useApp } from '../context/AppContext';
import { API_BASE } from '../lib/api';
import { useToast } from '../hooks/use-toast';

/**
 * DownloadMenu — opens the backend download endpoint as <a download> per stem.
 * Stems are MOCKED on the server (different demo files) until real music generation is wired.
 */
export default function DownloadMenu({ song, trigger, align = 'end' }) {
  const { t, lang } = useApp();
  const { toast } = useToast();
  const [mp4Loading, setMp4Loading] = useState(false);
  if (!song) return null;

  const safeTitle = (song.title?.[lang] || song.title?.en || 'song').replace(/[^a-z0-9\-_. ]/gi, '_');
  const stems = song.stemsAvailable !== false;

  const handle = (stem) => (e) => {
    e.preventDefault(); e.stopPropagation();
    const url = `${API_BASE}/songs/${song.id}/download?stem=${stem}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeTitle}${stem === 'master' ? '' : '-' + stem}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const exportMp4 = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (mp4Loading) return;
    setMp4Loading(true);
    toast({ title: lang === 'fr' ? 'Préparation de la vidéo MP4...' : 'Preparing MP4 video...', description: lang === 'fr' ? 'Peut prendre 30-90s la première fois' : 'May take 30-90s the first time' });
    try {
      const res = await fetch(`${API_BASE}/songs/${song.id}/export/mp4`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = `${safeTitle}.mp4`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
      toast({ title: lang === 'fr' ? 'Vidéo MP4 prête' : 'MP4 ready' });
    } catch (err) {
      toast({ title: lang === 'fr' ? 'Échec export MP4' : 'MP4 export failed', description: String(err.message || err) });
    } finally {
      setMp4Loading(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="bg-[#141416] border-white/10 text-zinc-200 w-60">
        <DropdownMenuLabel className="text-zinc-400 text-xs">{t('player.download')}</DropdownMenuLabel>
        <DropdownMenuItem onClick={handle('master')} className="gap-2 cursor-pointer">
          <Music2 size={14} /> {t('player.download_song')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={(e)=>{ e.preventDefault(); exportMp4(e); }} disabled={mp4Loading} className="gap-2 cursor-pointer">
          {mp4Loading ? <Loader2 size={14} className="animate-spin" /> : <Film size={14} />}
          {lang === 'fr' ? 'Exporter en MP4 (vidéo)' : 'Export as MP4 (video)'}
        </DropdownMenuItem>
        {stems && (
          <>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem onClick={handle('vocals')} className="gap-2 cursor-pointer">
              <Mic2 size={14} /> {t('player.download_vocals')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handle('instrumental')} className="gap-2 cursor-pointer">
              <Disc3 size={14} /> {t('player.download_instrumental')}
            </DropdownMenuItem>
            <div className="px-2 py-1.5 text-[11px] text-zinc-500">{t('player.stems_note')}</div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
